import { ref } from 'vue'
import type { Ref } from 'vue'
import {
  abortGalleryUpload,
  completeGalleryCoverUpload,
  deleteGalleryCover,
  reserveGalleryCoverUpload,
} from '@/api/galleries'
import { GalleryApiErrorCode } from '@/types/galleries'
import type { OrganizerGallery, UploadTicket } from '@/types/galleries'
import { parseGalleryApiError } from '@/utils/galleries/apiError'
import { UnsupportedImageError, downscaleImage } from '@/utils/media/downscaleImage'
import type { PreparedFile } from '@/utils/media/downscaleImage'
import { uploadToPresignedUrl } from '@/utils/media/uploadToPresignedUrl'

/** Error code reported when the chosen image cannot be decoded or converted in the browser. */
export const COVER_HEIC_UNSUPPORTED = 'heic_unsupported'
/** Error code reported when the presigned upload itself fails (network or storage error). */
export const COVER_UPLOAD_FAILED = 'upload_failed'
/** Error code reported when preparing (resizing) the image fails. */
export const COVER_PREPARE_FAILED = 'prepare_failed'

export interface UseGalleryCoverUpload {
  /** Prepares, uploads and completes one cover image. Resolves with the updated gallery, or null on failure. */
  upload: (galleryId: string, file: File) => Promise<OrganizerGallery | null>
  /** Removes the dedicated cover. Resolves true on success. */
  removeCover: (galleryId: string) => Promise<boolean>
  uploading: Ref<boolean>
  progress: Ref<number>
  /** Stable error code of the last failure, see GalleryApiErrorCode and the COVER_* constants. */
  errorCode: Ref<string | null>
}

export function useGalleryCoverUpload(eventSlug: string): UseGalleryCoverUpload {
  const uploading = ref(false)
  const progress = ref(0)
  const errorCode = ref<string | null>(null)

  async function abortQuietly(galleryId: string, ticket: UploadTicket): Promise<void> {
    try {
      await abortGalleryUpload(eventSlug, galleryId, ticket.id)
    } catch {
      // Best effort: the server expires stale reservations by itself.
    }
  }

  async function upload(galleryId: string, file: File): Promise<OrganizerGallery | null> {
    uploading.value = true
    progress.value = 0
    errorCode.value = null
    let ticket: UploadTicket | null = null

    try {
      let prepared: PreparedFile
      try {
        prepared = await downscaleImage(file, { keepOriginal: false })
      } catch (e) {
        errorCode.value = e instanceof UnsupportedImageError && e.heic ? COVER_HEIC_UNSUPPORTED : COVER_PREPARE_FAILED
        return null
      }

      try {
        ticket = await reserveGalleryCoverUpload(eventSlug, galleryId, {
          mime: prepared.mime,
          bytes: prepared.bytes,
          filename: prepared.name,
          ...(prepared.width > 0 && prepared.height > 0 ? { width: prepared.width, height: prepared.height } : {}),
        })
      } catch (e) {
        errorCode.value = parseGalleryApiError(e).code ?? GalleryApiErrorCode.FileTooLarge
        return null
      }

      const target = ticket.upload
      if (target === null) {
        errorCode.value = COVER_UPLOAD_FAILED
        await abortQuietly(galleryId, ticket)
        return null
      }

      try {
        await uploadToPresignedUrl(prepared.blob, target, (loaded, total) => {
          progress.value = total > 0 ? Math.min(99, Math.round((loaded / total) * 100)) : 0
        })
      } catch {
        errorCode.value = COVER_UPLOAD_FAILED
        await abortQuietly(galleryId, ticket)
        return null
      }

      try {
        const gallery = await completeGalleryCoverUpload(eventSlug, galleryId, ticket.id)
        progress.value = 100
        return gallery
      } catch (e) {
        errorCode.value = parseGalleryApiError(e).code ?? COVER_UPLOAD_FAILED
        await abortQuietly(galleryId, ticket)
        return null
      }
    } finally {
      uploading.value = false
    }
  }

  async function removeCover(galleryId: string): Promise<boolean> {
    errorCode.value = null
    try {
      await deleteGalleryCover(eventSlug, galleryId)
      return true
    } catch (e) {
      errorCode.value = parseGalleryApiError(e).code ?? COVER_UPLOAD_FAILED
      return false
    }
  }

  return { upload, removeCover, uploading, progress, errorCode }
}
